import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
// import ResponsiveDataManagement from "../../../Components/ResponsiveDataManagement/ResponsiveDataManagement";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import MobileCustomerForm from "../../Customer/Components/MobileCustomerForm";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import Select from "react-select";
import TableSearch from "../../../Components/Common/TableSearch";
import useIsSmallScreen from "../../../OrderingSystem/Payment Type/Component/small screen/SmallScreen";
import { Link, useNavigate } from "react-router-dom";

const Customers = () => {
  const [customerList, setCustomerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPageFromTable, setCurrentPageFromTable] = useState(1);
  const [perPageFromTable, setPerPageFromTable] = useState(10);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const isSmallScreen = useIsSmallScreen();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isDesktop = windowWidth >= 1024;

  // Summary statistics
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalSpendings, setTotalSpending] = useState(0);
  const [topSpenderAmount, setTopSpendorAmount] = useState(0);
  const [totalDiscountAmount, setTotalDiscountAmount] = useState(0);
  const [customerTypeFilter, setCustomerTypeFilter] = useState("");

  const navigate = useNavigate();

  // Columns for Data Table
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPageFromTable - 1) * perPageFromTable + index + 1,
      width: "75px",
      sortable: false,
    },
    {
      name: "Name",
      selector: (row) => `${row.firstName} ${row.lastName}`,
      cell: (row) => (
        <Link to={`/customer/${row?.id}`}>
          {row.firstName} {row.lastName}
        </Link>
      ),
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone No",
      selector: (row) => row.phoneNo,
      sortable: true,
    },
    {
      name: "Customer Type",
      selector: (row) => row.customerTypeName || "-",
      cell: (row) => row.customerTypeName || "-",
      sortable: true,
    },
    {
      name: "Average Order",
      selector: (row) => row.averageOrderValue || "0",
      cell: (row) => row.averageOrderValue || "0",
      sortable: true,
      right: true,
    },
    {
      name: "Total Orders",
      selector: (row) => row.totalOrders || "0",
      sortable: true,
      right: true,
    },
    {
      name: "Total Amount",
      selector: (row) => row.totalOrderAmount || "0",
      sortable: true,
      right: true,
    },

    {
      name: "Discount (%)",
      selector: (row) => row.customerTypeDiscount || 0,
      sortable: true,
      right: true,
    },
  ];

  // Stats cards
  const stats = [
    {
      title: "Total Customers",
      value: totalCustomers,
      color: "#0d6efd",
      icon: "bx bx-user",
    },
    {
      title: "Total Spendings",
      value: totalSpendings,
      color: "#28a745",
      icon: "bx bx-purchase-tag",
    },
    {
      title: "Top Spendor Amount",
      value: topSpenderAmount,
      color: "#17a2b8",
      icon: "bx bx-category",
    },
    {
      title: "Total Discount Amount",
      value: totalDiscountAmount,
      color: "#ffc107",
      icon: "bx bx-dollar",
    },
  ];

  // Fetch customers API
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await CustomerServices.getCustomerLists();
      setCustomerList(response.customers || []);

      setTotalCustomers(response.summary?.totalCustomers || 0);
      setTotalSpending(response.summary?.totalSpendings || 0);
      setTopSpendorAmount(response.summary?.topSpenderAmount || 0);
      setTotalDiscountAmount(response.summary?.totalDiscountAmount || 0);

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Handlers
  const handleView = (customer) => {
    navigate(`/customer/${customer.id}`);
  };
  const handleAdd = () => setAddModal(true);
  const handleEdit = async (customer) => {
    try {
      const fullCustomer = await CustomerServices.getCustomerById(customer.id);
      setSelectedCustomer(fullCustomer);
      setAddModal(true);
    } catch (error) {
      console.error("Error fetching full customer data", error);
    }
  };

  const handleDelete = async (customer) => {
    try {
      await CustomerServices.delete(customer.id);
      setCustomerList((prev) => prev.filter((c) => c.id !== customer.id));
    } catch (error) {}
  };

  const filteredCustomerList = customerList.filter((item) => {
    const matchesCustomerType = customerTypeFilter
      ? item.customerTypeName?.toLowerCase() ===
        customerTypeFilter.toLowerCase()
      : true;

    return matchesCustomerType;
  });

  const customerTypeOptions = Array.from(
    new Set(customerList.map((c) => c.customerTypeName).filter(Boolean)),
  ).map((typeName) => ({
    value: typeName,
    label: typeName,
  }));

  const handleCustomerTypeFilterChange = (value) => {
    setCustomerTypeFilter(value);
  };

  const renderHeader = () => {
    const searchControl = (
      <div style={{ flex: '1 1 auto', minWidth: '200px', maxWidth: isDesktop ? '300px' : 'none' }}>
        <TableSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search customers..."
        />
      </div>
    );

    const filterControl = (
      <div style={{ minWidth: "180px" }}>
        <Select
          options={customerTypeOptions}
          value={customerTypeOptions.find(opt => opt.value === customerTypeFilter)}
          onChange={(opt) => handleCustomerTypeFilterChange(opt?.value || "")}
          placeholder="All Customer"
          isClearable={true}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({
              ...base,
              minHeight: "38px",
              borderRadius: "8px",
              borderColor: "#dee2e6",
              fontSize: "0.85rem"
            })
          }}
          menuPortalTarget={document.body}
        />
      </div>
    );

    const actionButtons = (
      <div className="d-flex gap-2">
         <Button
          color="soft-success"
          onClick={() => navigate("/customer-import")}
          className="d-flex align-items-center gap-2"
          style={{ borderRadius: "8px", fontSize: "0.85rem", height: "38px" }}
        >
          <i className="ri-file-excel-2-fill"></i>
          <span>Excel</span>
        </Button>
        <Button
          color="success"
          onClick={handleAdd}
          className="d-flex align-items-center gap-2"
          style={{ borderRadius: "8px", fontSize: "0.85rem", height: "38px", whiteSpace: "nowrap" }}
        >
          <i className="bx bx-plus" />
          <span>Add Customer</span>
        </Button>
       
      </div>
    );

    if (isDesktop) {
      return (
        <Row className="align-items-center">
          <Col lg={4}>
             <h5 className="mb-0 fw-bold text-dark">Customers List</h5>
             <p className="text-muted small mb-0">Manage your user base and their activity</p>
          </Col>
          <Col lg={8}>
            <div className="d-flex justify-content-end align-items-center gap-2">
              {searchControl}
              {filterControl}
              {actionButtons}
            </div>
          </Col>
        </Row>
      );
    }

    return (
      <div className="bg-white p-3 rounded-3 shadow-sm mb-3 border">
        <div className="d-flex justify-content-between align-items-center mb-3">
           <h5 className="mb-0 fw-bold text-dark">Customers</h5>
           {actionButtons}
        </div>
        <div className="d-flex gap-2 flex-wrap">
           {searchControl}
           {filterControl}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Mobile/Tablet Header rendered outside because ResponsiveDataManagement hides customHeaderContent */}
      {!isDesktop && <div className="p-3 pb-0">{renderHeader()}</div>}

      <ResponsiveDataManagement
        title={isDesktop ? "Customers" : ""}
        pageTitle={isDesktop ? "User Management" : ""}
        mobileCardTitle={null}
        data={filteredCustomerList.map((c) => ({
          ...c,
          onClick: () => navigate(`/customer/${c.id}`),
        }))}
        columns={columns}
        stats={stats}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchKeys={[
          "firstName",
          "lastName",
          "email",
          "phoneNo",
          "customerTypeName",
        ]}
        onAdd={null} // Handled by custom header
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading}
        emptyStateMessage="No customers found"
        showSearch={false} // Handled by custom header
        onSelect={null} // Handled by custom header
        customHeaderContent={isDesktop ? renderHeader() : null}
        currentPage={currentPageFromTable}
        pageSize={perPageFromTable}
        mobileCardRenderer={(item) => (
          <Card
            className="shadow-sm rounded-3 border-0 border-start border-4 border-success"
            onClick={() => handleView(item)} // Card click handler
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="fw-bold text-primary mb-1">
                    {item.firstName} {item.lastName}
                  </h6>

                  <p className="mb-1 text-muted small">
                    <i className="bx bx-envelope me-1" /> {item.email || "-"}
                  </p>

                  <p className="mb-2 text-muted small">
                    <i className="bx bx-phone me-1" /> {item.phoneNo || "-"}
                  </p>

                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    {item.customerTypeName && (
                      <span className="badge bg-info">
                        {item.customerTypeName}
                      </span>
                    )}

                    {item.customerTypeDiscount > 0 && (
                      <span className="badge bg-success">
                        {item.customerTypeDiscount}% Discount
                      </span>
                    )}
                  </div>
                </div>

                {/* Mobile dropdown */}
                <Dropdown
                  isOpen={openDropdownId === item.id}
                  toggle={() =>
                    setOpenDropdownId(
                      openDropdownId === item.id ? null : item.id,
                    )
                  }
                  direction="down"
                >
                  <DropdownToggle
                    color="light"
                    className="btn-sm rounded-circle shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>

                  <DropdownMenu end>
                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-eye-fill me-2" />
                      View
                    </DropdownItem>

                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-edit-line me-2" />
                      Edit
                    </DropdownItem>

                    {/* <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-delete-bin-line me-2" />
                      Delete
                    </DropdownItem> */}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        )}
        onPaginationChange={({ page, perPage }) => {
          console.log("🚀 ~ Customers ~ perPage:", perPage)
          setCurrentPageFromTable(page); // save current page
          setPerPageFromTable(perPage); // save rows per page
        }}
      />

      {/* Mobile Add/Edit Form */}
      <MobileCustomerForm
        modal={addModal}
        toggleModal={() => {
          setAddModal(false);
          setSelectedCustomer(null);
        }}
        setCustomerList={setCustomerList}
        customerList={customerList}
        selectedCustomer={selectedCustomer}
        fetchCustomers={fetchCustomers}
      />

      {/* Bulk Excel Upload Modal - Replaced by page */}
      {/* <CustomerBulkUploadModal
        isOpen={bulkUploadModal}
        toggle={() => setBulkUploadModal(false)}
        onSuccess={fetchCustomers}
      /> */}
    </>
  );
};

export default Customers;
